import { SiSap } from "react-icons/si";
import { LiaSalesforce } from "react-icons/lia";
import { Search16Regular } from "@fluentui/react-icons";

// Pick a useful search term from a filename for the Dokumentensuche.
// Prefers a leading numeric prefix ("5078230_..." → "5078230"); otherwise
// picks the longest digit run in the name (covers patterns like
// "betriebsanleitung p1500 10002955.pdf" → "10002955"). Falls back to the
// filename without its extension, which still produces a reasonable
// document-name search.
const extractSearchTerm = (fileName?: string): string => {
    if (!fileName) return "";
    const leading = fileName.match(/^(\d+)/);
    if (leading) return leading[1];
    const digitRuns = fileName.match(/\d{4,}/g);
    if (digitRuns && digitRuns.length > 0) {
        return digitRuns.reduce((a, b) => (b.length > a.length ? b : a));
    }
    return fileName.replace(/\.[^.]+$/, "");
};

// Build the Dokumentensuche deep-link URL using the same encoding contract
// as app/frontend/src/pages/search/Search.tsx (encodeFiltersToUrl).
const buildDokumentensucheUrl = (
    searchResult: { documenttype?: string; newProduct?: string; product?: string; sourcesystem?: string },
    searchTerm: string
): string => {
    const filters: Record<string, string[]> = {};
    if (searchResult.documenttype) filters.documenttype = [searchResult.documenttype];
    const productValue = searchResult.newProduct || searchResult.product;
    if (productValue) filters.newProduct = [productValue];
    if (searchResult.sourcesystem) filters.sourcesystem = [searchResult.sourcesystem];

    const params = new URLSearchParams();
    params.set("page", "1");
    if (Object.keys(filters).length > 0) {
        params.set("filters", btoa(JSON.stringify(filters)));
    }
    if (searchTerm) params.set("q", searchTerm);
    return `${window.location.pathname}#/search?${params.toString()}`;
};

interface CitationPillProps {
    citation: string;
    searchResult: any;
    onCitationClicked: (filePath: string) => void;
    onShareClicked?: (sharepointUrl: string, fileName: string) => void;
    inline?: boolean; // true for inline pills in text, false for bottom list
}

/**
 * Shared component for rendering citation pills consistently across the app.
 * Handles SAP tickets, Salesforce tickets, and regular documents.
 * Supports both inline (in answer text) and list (bottom citations) rendering.
 */
export const CitationPill = ({ citation, searchResult, onCitationClicked, onShareClicked, inline = false }: CitationPillProps) => {
    if (!searchResult) return null;

    const fileName = searchResult?.sourcefile;
    const baseClasses = inline 
        ? "inline-flex items-center px-2 py-0.5 mx-1 bg-blue-50 text-xs hover:bg-blue-100 cursor-pointer text-blue-600 border border-blue-200"
        : "inline-flex items-center px-2 py-0.5 bg-blue-50 text-xs hover:bg-blue-100 cursor-pointer text-blue-600 border border-blue-200";

    // SAP Ticket
    if (citation.startsWith('sap_') || searchResult?.sourcesystem === "SAP") {
        const ticketId = citation.startsWith('sap_') 
            ? citation.replace('sap_', '') 
            : fileName.replace(".txt", "").replaceAll("_", " ");
        
        let filePath = '';
        try {
            if (searchResult?.storageUrl && searchResult.storageUrl.trim() !== '') {
                filePath = new URL(searchResult.storageUrl).pathname;
            }
        } catch (error) {
            console.warn('Invalid storageUrl:', searchResult?.storageUrl, error);
        }

        // Fallback construction if storageUrl missing
        if (!filePath && searchResult?.sourcefile) {
            filePath = `raw/sap3/${searchResult.sourcefile}`;
        }

        const finalPath = filePath ? `/content/${filePath}` : '';

        return (
            <a
                className={`${baseClasses} ${inline ? '' : 'rounded'}`}
                title={`SAP Ticket ${ticketId}`}
                onClick={(e) => {
                    if (inline) e.preventDefault();
                    if (finalPath) {
                        onCitationClicked(finalPath);
                    }
                }}
            >
                <SiSap className={inline ? "mr-1 w-3 h-3" : "mx-1 w-4 h-4"} />
                <span className="font-medium">{ticketId}</span>
            </a>
        );
    }

    // Salesforce Ticket
    if (citation.startsWith('sf_') || searchResult?.sourcesystem === "Salesforce") {
        const ticketId = citation.startsWith('sf_') 
            ? citation.replace('sf_', '') 
            : fileName.replace(".txt", "");
        const salesforceUrl = `https://weinig.lightning.force.com/lightning/r/Case/${ticketId}/view`;
        
        let filePath = '';
        try {
            if (searchResult?.storageUrl && searchResult.storageUrl.trim() !== '') {
                filePath = new URL(searchResult.storageUrl).pathname;
            }
        } catch (error) {
            console.warn('Invalid storageUrl:', searchResult?.storageUrl, error);
        }

        // Fallback guess if storage url missing
        if (!filePath && searchResult?.sourcefile) {
            const sourceFile = searchResult.sourcefile.endsWith('.txt') 
                ? searchResult.sourcefile 
                : `${ticketId}.txt`;
            filePath = `raw/sf3/${sourceFile}`;
        }

        const finalPath = filePath ? `/content/${filePath}` : '';

        return (
            <span className={inline ? "inline-flex items-center mx-1" : "inline-flex items-center"}>
                <a
                    className={`${baseClasses} rounded-l`}
                    title={`Salesforce Ticket ${ticketId}`}
                    onClick={(e) => {
                        if (inline) e.preventDefault();
                        if (finalPath) {
                            onCitationClicked(finalPath);
                        }
                    }}
                >
                    <LiaSalesforce className={inline ? "mr-1 w-3 h-3" : "mx-1 w-4 h-4"} />
                    <span className="font-medium">{ticketId}</span>
                </a>
                <a
                    href={salesforceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center px-1.5 py-0.5 bg-blue-50 text-xs rounded-r hover:bg-blue-100 text-blue-600 border-t border-r border-b border-blue-200 border-l-0`}
                    title="Open in Salesforce"
                    onClick={(e) => e.stopPropagation()}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={inline ? "h-3 w-3" : "h-4 w-4"}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 3h7v7m0 0L10 21l-7-7 11-11z" />
                    </svg>
                </a>
            </span>
        );
    }

    // Regular Document (PDF, Excel, Word, etc.)
    let originUrl = '';
    let storagePath = '';
    
    try {
        if (searchResult?.originUrl && searchResult.originUrl.trim() !== '') {
            originUrl = searchResult.originUrl;
        }
    } catch (error) {
        console.warn('Invalid originUrl:', searchResult?.originUrl, error);
    }

    try {
        if (searchResult?.storageUrl && searchResult.storageUrl.trim() !== '') {
            storagePath = new URL(searchResult.storageUrl).pathname.replace(/^\/+/, '');
        }
    } catch (error) {
        // storageUrl not critical here
    }

    // Check if file type is supported for in-app viewing (pdf, txt, md)
    const fileExtension = fileName?.toLowerCase().split('.').pop() || '';
    const supportedExtensions = ['pdf', 'txt', 'md'];
    const isSupported = supportedExtensions.includes(fileExtension);

    // Append a `filename=<original sourcefile>` query param so the backend
    // serves the file with a human-readable Content-Disposition. Without
    // this, browsers suggest the blob's UUID as the Save-As name.
    const fileNameQuery = fileName ? `?filename=${encodeURIComponent(fileName)}` : '';

    // Decide what to pass to onCitationClicked
    let citationTarget: string;
    if (originUrl) {
        try {
            citationTarget = new URL(originUrl).pathname; // triggers sharepoint flow
        } catch {
            citationTarget = originUrl;
        }
    } else if (storagePath) {
        citationTarget = `/content/${storagePath}${fileNameQuery}`;
    } else {
        citationTarget = `/content/${fileName}${fileNameQuery}`;
    }

    // Handler for citation click
    const handleCitationClick = (e: React.MouseEvent) => {
        if (inline) e.preventDefault();
        
        if (!isSupported && originUrl && originUrl.trim() !== '') {
            // Unsupported file: open directly in SharePoint
            window.open(originUrl, '_blank', 'noopener,noreferrer');
        } else if (citationTarget) {
            // Supported file: use viewer
            onCitationClicked(citationTarget);
        }
    };

    return (
        <span className={inline ? "inline-flex items-center mx-1" : "inline-flex items-center"}>
            <a
                className={`${baseClasses} ${isSupported || !originUrl ? 'rounded' : 'rounded-l'}`}
                title={fileName}
                onClick={handleCitationClick}
            >
                <svg className={inline ? "mr-1 w-3 h-3" : "mr-1 w-3 h-3"} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                </svg>
                <span className="font-medium truncate max-w-xs">{fileName}</span>
            </a>

            {/* Only show arrow icon for supported file types */}
            {isSupported && originUrl && originUrl.trim() !== '' && (
                <a
                    href={originUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center px-1.5 py-0.5 bg-blue-50 text-xs hover:bg-blue-100 text-blue-600 border-t border-r border-b border-blue-200 border-l-0 ${
                        inline ? '' : ''
                    }`}
                    title="Datei in SharePoint öffnen"
                    onClick={(e) => e.stopPropagation()}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={inline ? "h-3 w-3" : "h-4 w-4"}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 3h7v7m0 0L10 21l-7-7 11-11z" />
                    </svg>
                </a>
            )}

            {/* Dokumentensuche deep-link — surfaces sibling language variants. SharePoint sources only. */}
            {searchResult?.sourcesystem === "Sharepoint" && (
                <a
                    href={buildDokumentensucheUrl(searchResult, extractSearchTerm(fileName))}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-1.5 py-0.5 bg-blue-50 text-xs hover:bg-blue-100 text-blue-600 border-t border-r border-b border-blue-200 border-l-0"
                    title="In Dokumentensuche suchen"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Search16Regular className={inline ? "h-3 w-3" : "h-4 w-4"} />
                </a>
            )}

            {/* Folder icon to open SharePoint folder - use folderlink from search index, fallback to trimming originUrl */}

            {(searchResult?.folderlink || originUrl) && (
                <a
                    href={(() => {
                        // Use folderlink if available
                        if (searchResult?.folderlink && searchResult.folderlink.trim() !== '') {
                            return searchResult.folderlink;
                        }
                        // Fallback: trim originUrl to get folder path
                        if (originUrl) {
                            try {
                                const url = new URL(originUrl);
                                url.pathname = url.pathname.substring(0, url.pathname.lastIndexOf("/")) + "/";
                                return url.toString();
                            } catch {
                                return originUrl;
                            }
                        }
                        return '';
                    })()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center px-1.5 py-0.5 bg-blue-50 text-xs hover:bg-blue-100 text-yellow-600 border-t border-r border-b border-blue-200 border-l-0`}
                    title="Ordner in SharePoint öffnen"
                    onClick={(e) => e.stopPropagation()}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={inline ? "h-3 w-3" : "h-4 w-4"}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7a2 2 0 012-2h3.172a2 2 0 011.414.586l1.828 1.828A2 2 0 0012.828 8H19a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                    </svg>
                </a>
            )}

            {/* Share externally button — SharePoint-sourced docs only */}
            {onShareClicked && originUrl && originUrl.trim() !== '' && (
                <button
                    type="button"
                    className="inline-flex items-center px-1.5 py-0.5 bg-blue-50 text-xs rounded-r hover:bg-blue-100 text-green-700 border-t border-r border-b border-blue-200 border-l-0"
                    title="Extern teilen"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onShareClicked(originUrl, fileName);
                    }}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={inline ? "h-3 w-3" : "h-4 w-4"}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                </button>
            )}
        </span>
    );
};
